'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  tier: 'Standard' | 'Gold' | 'VIP';
  rating: number;
  quote: string;
  project: string;
  avatar: string;
  avatarAlt: string;
}

const testimonials: Testimonial[] = [
{
  id: 't1',
  name: 'Priya Mehta',
  role: 'VP of Marketing',
  company: 'Meridian Capital',
  tier: 'VIP',
  rating: 5,
  quote:
  'NASARC didn\'t just make our brand film — they rewrote how we think about content. The Meridian launch piece has become our most-shared asset internally and externally. The team operates at a level that makes other agencies look like they\'re still figuring it out.',
  project: 'Meridian Brand Launch',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_175bc78da-1763297795096.png",
  avatarAlt: 'Professional South Asian woman with dark hair in business attire, confident expression'
},
{
  id: 't2',
  name: 'Soren Hald',
  role: 'Creative Director',
  company: 'Crōss Apparel',
  tier: 'Gold',
  rating: 5,
  quote:
  'The Atacama shoot was logistically insane — 3 days, remote location, unpredictable weather. NASARC handled every challenge without missing a single frame. The footage we got back was beyond what our internal team could have imagined. We\'re already planning the next one.',
  project: 'Crōss SS26 Campaign',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc4bf3ea-1773076694826.png",
  avatarAlt: 'Young European man with light stubble, creative professional in casual dark outfit'
},
{
  id: 't3',
  name: 'Amara Osei',
  role: 'Head of Brand',
  company: 'Lumena Wellness',
  tier: 'Gold',
  rating: 5,
  quote:
  'Eight episodes, delivered on time, every one of them consistent in tone and quality. NASARC\'s post team treated our docuseries like it was going to Sundance. The color grade alone elevated the whole thing. We\'re renewing our Gold tier without question.',
  project: 'Lumena Lifestyle Series',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5b689fb-1763296744210.png",
  avatarAlt: 'Professional African woman with natural hair, warm smile, creative industry professional'
},
{
  id: 't4',
  name: 'Kenji Watanabe',
  role: 'CEO',
  company: 'Axiom Labs',
  tier: 'Standard',
  rating: 5,
  quote:
  'We came to NASARC for a product demo video and left with a full AI content pipeline. The AI-synthesized voiceover was indistinguishable from a human read, and the UI animations they produced cut our production timeline by 60%. Incredible ROI.',
  project: 'Axiom AI Product Demo',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d61392ba-1763296336232.png",
  avatarAlt: 'Japanese man in his 30s with glasses, tech startup CEO in smart casual attire'
}];


const tierColors: Record<string, string> = {
  Standard: 'text-studio-300 bg-studio-700/60 border-studio-600/40',
  Gold: 'text-gold-400 bg-gold-500/10 border-gold-500/25',
  VIP: 'text-white bg-white/8 border-white/20'
};

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const autoRef = useRef<NodeJS.Timeout | null>(null);

  const go = (dir: 'prev' | 'next') => {
    if (animating) return;
    setAnimating(true);
    setActive((prev) => {
      if (dir === 'next') return (prev + 1) % testimonials.length;
      return (prev - 1 + testimonials.length) % testimonials.length;
    });
    setTimeout(() => setAnimating(false), 400);
  };

  useEffect(() => {
    autoRef.current = setInterval(() => go('next'), 6000);
    return () => {if (autoRef.current) clearInterval(autoRef.current);};
  }, []);

  const t = testimonials[active];

  return (
    <section className="py-24 xl:py-32 bg-studio-900 border-y border-studio-700/50" aria-label="Client testimonials">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        <div className="text-center mb-12">
          <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Testimonials</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl text-white">
            Clients Who <span className="gradient-text-gold">Trust Us</span>
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Main testimonial */}
          <div
            className={`relative bg-studio-950 rounded-2xl border border-studio-700/50 p-8 xl:p-12 mb-6 transition-all duration-400 ${
            animating ? 'opacity-60 scale-[0.99]' : 'opacity-100 scale-100'}`
            }>
            
            {/* Quote icon */}
            <div className="absolute top-6 right-8 xl:top-10 xl:right-12 opacity-10">
              <Quote size={64} className="text-gold-400" />
            </div>

            {/* Stars */}
            <div className="flex items-center gap-1 mb-6">
              {Array.from({ length: t.rating }).map((_, i) =>
              <Star key={i} size={16} className="text-gold-400 fill-gold-400" />
              )}
            </div>

            {/* Quote */}
            <blockquote className="text-studio-100 text-lg md:text-xl leading-relaxed font-light mb-8 relative z-10">
              "{t.quote}"
            </blockquote>

            {/* Project tag */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-studio-500 text-xs">Re:</span>
              <span className="px-2.5 py-1 bg-studio-800/60 border border-studio-700/40 rounded-full text-studio-300 text-xs font-medium">
                {t.project}
              </span>
            </div>

            {/* Author */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-studio-700/60 shrink-0">
                <AppImage
                  src={t.avatar}
                  alt={t.avatarAlt}
                  width={48}
                  height={48}
                  className="object-cover w-full h-full" />
                
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{t.name}</p>
                <p className="text-studio-400 text-xs">{t.role} · {t.company}</p>
              </div>
              <div className="ml-auto">
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${tierColors[t.tier]}`}>
                  {t.tier === 'VIP' && <span>👑</span>}
                  {t.tier === 'Gold' && <Star size={10} className="fill-current" />}
                  {t.tier} Client
                </span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) =>
              <button
                key={i}
                onClick={() => {setActive(i);if (autoRef.current) {clearInterval(autoRef.current);autoRef.current = setInterval(() => go('next'), 6000);}}}
                className={`transition-all duration-300 rounded-full ${
                i === active ?
                'w-6 h-2 bg-gold-400' : 'w-2 h-2 bg-studio-600 hover:bg-studio-500'}`
                }
                aria-label={`Go to testimonial ${i + 1}`} />

              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => go('prev')}
                className="w-9 h-9 rounded-xl bg-studio-800/60 border border-studio-700/50 text-studio-300 hover:text-white hover:border-studio-600/60 hover:bg-studio-700/60 flex items-center justify-center transition-all duration-200 active:scale-95"
                aria-label="Previous testimonial">
                
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => go('next')}
                className="w-9 h-9 rounded-xl bg-studio-800/60 border border-studio-700/50 text-studio-300 hover:text-white hover:border-studio-600/60 hover:bg-studio-700/60 flex items-center justify-center transition-all duration-200 active:scale-95"
                aria-label="Next testimonial">
                
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="grid grid-cols-4 gap-3 mt-6">
            {testimonials.map((item, i) =>
            <button
              key={item.id}
              onClick={() => setActive(i)}
              className={`p-3 rounded-xl border text-left transition-all duration-200 ${
              i === active ?
              'bg-studio-800/80 border-gold-500/30' : 'bg-studio-900/60 border-studio-700/30 hover:border-studio-600/50 hover:bg-studio-800/40'}`
              }>
              
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full overflow-hidden shrink-0">
                    <AppImage
                    src={item.avatar}
                    alt={item.avatarAlt}
                    width={24}
                    height={24}
                    className="object-cover w-full h-full" />
                  
                  </div>
                  <span className="text-white text-[11px] font-medium truncate">{item.name}</span>
                </div>
                <p className="text-studio-500 text-[10px] truncate">{item.company}</p>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>);

}